import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import "./css/CategoryPayment.css";

export default function CategoryPayment({
  onPaymentSuccess,
  initialSubCategories = [],
  initialSpecialisations = [],
  initialWorkAmount = null,
  disabledSubCategories = [],
  disabledSpecialisations = [],
  lang = "en",
}) {
  const [categories, setCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [allSpecialisations, setAllSpecialisations] = useState([]);
  const [selectedSpecialisations, setSelectedSpecialisations] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState(null); // 'valid', 'invalid', null
  const [couponData, setCouponData] = useState(null);
  const [fetchingCategories, setFetchingCategories] = useState(true);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showPaymentOverlay, setShowPaymentOverlay] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripePublishableKey, setStripePublishableKey] = useState(null);
  const [stripeOrderDetails, setStripeOrderDetails] = useState(null);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      searchCategories: "Search Categories",
      couponCode: "Coupon Code",
      enterCoupon: "Enter coupon code",
      couponApplied: "Coupon Applied Successfully!",
      couponNotAvailable: "Coupon Not Available!",
      total: "Total",
      discount: "Discount",
      subTotal: "Sub Total",
      payNow: "Pay Now",
      save: "Save",
      minWorkAmount: "Min Work Amount (AED)",
      maxWorkAmount: "Max Work Amount (AED)",
      enterMinAmount: "Enter minimum work amount",
      enterMaxAmount: "Enter maximum work amount",
      invalidAmount: "Please enter valid amounts",
      minPaymentAmount: "Minimum amount for payment should be 2 AED",
      selectingCategories: "Please select at least one category",
      paymentSuccess: "Payment successful!",
      paymentFailed: "Payment failed. Please try again.",
      validatingCoupon: "Validating...",
      processingPayment: "Processing...",
    },
    ar: {
      searchCategories: "البحث عن الفئات",
      couponCode: "رمز القسيمة",
      enterCoupon: "أدخل رمز القسيمة",
      couponApplied: "تم تطبيق القسيمة بنجاح!",
      couponNotAvailable: "القسيمة غير متاحة!",
      total: "المجموع",
      discount: "الخصم",
      subTotal: "المجموع الفرعي",
      payNow: "ادفع الآن",
      save: "حفظ",
      minWorkAmount: "الحد الأدنى لمبلغ العمل (درهم)",
      maxWorkAmount: "الحد الأقصى لمبلغ العمل (درهم)",
      enterMinAmount: "أدخل الحد الأدنى لمبلغ العمل",
      enterMaxAmount: "أدخل الحد الأقصى لمبلغ العمل",
      invalidAmount: "يرجى إدخال مبالغ صحيحة",
      minPaymentAmount: "الحد الأدنى للدفع يجب أن يكون 2 درهم",
      selectingCategories: "يرجى اختيار فئة واحدة على الأقل",
      paymentSuccess: "تم الدفع بنجاح!",
      paymentFailed: "فشل الدفع. يرجى المحاولة مرة أخرى.",
      validatingCoupon: "جارٍ التحقق...",
      processingPayment: "جارٍ المعالجة...",
    },
  }[lang];

  // Fetch categories and sub-categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setFetchingCategories(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/sub-category/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const { categories: fetchedCategories = [], specialisations = [] } = data.response;
          setCategories(fetchedCategories);
          setAllSpecialisations(specialisations);
          
          // Initialize expanded categories
          const expanded = {};
          fetchedCategories.forEach((cat) => {
            expanded[cat._id] = true;
          });
          setExpandedCategories(expanded);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        showToast(err.message || "Failed to load categories", "error");
      } finally {
        setFetchingCategories(false);
      }
    };

    fetchCategories();
  }, [USER_BASE_URL, VERSION]);

  // Initialize with existing data
  useEffect(() => {
    if (initialSubCategories && initialSubCategories.length > 0) {
      setSelectedSubCategories(initialSubCategories);
    }
    if (initialSpecialisations && initialSpecialisations.length > 0) {
      setSelectedSpecialisations(initialSpecialisations);
    }
    if (initialWorkAmount) {
      setMinAmount(initialWorkAmount.min?.toString() || "");
      setMaxAmount(initialWorkAmount.max?.toString() || "");
    }
  }, [initialSubCategories, initialSpecialisations, initialWorkAmount]);

  // Calculate total amount
  const calculateTotal = () => {
    if (!categories || categories.length === 0) return 0;
    
    // Calculate total for all selected sub-categories
    let subCategoryTotal = 0;
    selectedSubCategories.forEach((subCatId) => {
      categories.forEach((cat) => {
        const subCat = cat.sub_categories?.find((sc) => sc._id === subCatId);
        if (subCat && subCat.price) {
          subCategoryTotal += subCat.price;
        }
      });
    });

    // Subtract already purchased sub-categories
    let alreadyPurchasedTotal = 0;
    disabledSubCategories.forEach((subCatId) => {
      categories.forEach((cat) => {
        const subCat = cat.sub_categories?.find((sc) => sc._id === subCatId);
        if (subCat && subCat.price) {
          alreadyPurchasedTotal += subCat.price;
        }
      });
    });

    // Calculate total for all selected specialisations
    let specialisationTotal = 0;
    selectedSpecialisations.forEach((specId) => {
      const spec = allSpecialisations.find((s) => s._id === specId);
      if (spec && spec.price && !disabledSpecialisations.includes(specId)) {
        specialisationTotal += spec.price;
      }
    });

    // Calculate 1% of max amount
    const maxAmountNum = parseFloat(maxAmount) || 0;
    const onePercentOfMax = maxAmountNum * 0.01;
    
    // Subtract 1% of initial max amount if it exists
    let onePercentOfInitialMax = 0;
    if (initialWorkAmount && initialWorkAmount.max) {
      onePercentOfInitialMax = initialWorkAmount.max * 0.01;
    }
    
    // Final calculation:
    // (All selected sub-category prices - Already purchased sub-category prices)
    // + (All selected specialisation prices - Already purchased specialisation prices [handled above])
    // + (1% of new max - 1% of old max)
    return (subCategoryTotal - alreadyPurchasedTotal) + specialisationTotal + (onePercentOfMax - onePercentOfInitialMax);
  };

  // Calculate discount from coupon
  const calculateDiscount = () => {
    if (!couponData || couponStatus !== "valid") return 0;

    const total = calculateTotal();
    if (couponData.type === "percentage") {
      return (total * couponData.amount) / 100;
    } else if (couponData.type === "fixed") {
      return couponData.amount;
    }
    return 0;
  };

  // Calculate final amount
  const calculateFinalAmount = () => {
    const total = calculateTotal();
    const discount = calculateDiscount();
    return Math.max(0, total - discount);
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Toggle sub-category selection
  const toggleSubCategory = (subCategoryId) => {
    if (disabledSubCategories.includes(subCategoryId)) return; // Disabled if already purchased
    
    setSelectedSubCategories((prev) => {
      if (prev.includes(subCategoryId)) {
        return prev.filter((id) => id !== subCategoryId);
      } else {
        return [...prev, subCategoryId];
      }
    });
    
    // Clear coupon when selection changes
    setCouponCode("");
    setCouponStatus(null);
    setCouponData(null);
  };

  // Toggle specialisation selection
  const toggleSpecialisation = (specialisationId) => {
    if (disabledSpecialisations.includes(specialisationId)) return;
    setSelectedSpecialisations((prev) => {
      if (prev.includes(specialisationId)) {
        return prev.filter((id) => id !== specialisationId);
      } else {
        return [...prev, specialisationId];
      }
    });
    // Clear coupon when selection changes
    setCouponCode("");
    setCouponStatus(null);
    setCouponData(null);
  };

  // Remove selected specialisation
  const removeSpecialisation = (specialisationId) => {
    if (disabledSpecialisations.includes(specialisationId)) return;
    setSelectedSpecialisations((prev) => prev.filter((id) => id !== specialisationId));
    setCouponCode("");
    setCouponStatus(null);
    setCouponData(null);
  };

  // Remove selected sub-category
  const removeSubCategory = (subCategoryId) => {
    if (disabledSubCategories.includes(subCategoryId)) return;
    setSelectedSubCategories((prev) => prev.filter((id) => id !== subCategoryId));
    setCouponCode("");
    setCouponStatus(null);
    setCouponData(null);
  };

  // Get sub-category name
  const getSubCategoryName = (subCategoryId) => {
    for (const cat of categories) {
      const subCat = cat.sub_categories?.find((sc) => sc._id === subCategoryId);
      if (subCat) {
        return `${cat.name} - ${subCat.name}`;
      }
    }
    return "";
  };

  // Validate coupon - returns promise that resolves with validation result
  const validateCoupon = async (codeToValidate = null) => {
    const code = codeToValidate || couponCode.trim();
    if (!code) {
      setCouponStatus(null);
      setCouponData(null);
      return { valid: false, coupon: null };
    }

    try {
      setValidatingCoupon(true);
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/coupon/`,
        "POST",
        JSON.stringify({ coupon: code }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200 && data.response) {
        const coupon = data.response;
        const total = calculateTotal();
        
        // Check if coupon meets minimum amount requirement
        if (coupon.min_amount && total < coupon.min_amount) {
          setCouponStatus("invalid");
          setCouponData(null);
          showToast(`Minimum amount required: AED ${coupon.min_amount}`, "error");
          return { valid: false, coupon: null };
        }

        // Check if coupon is expired
        if (coupon.expiry && new Date(coupon.expiry) < new Date()) {
          setCouponStatus("invalid");
          setCouponData(null);
          showToast(t.couponNotAvailable, "error");
          return { valid: false, coupon: null };
        }

        setCouponStatus("valid");
        setCouponData(coupon);
        return { valid: true, coupon };
      } else {
        setCouponStatus("invalid");
        setCouponData(null);
        return { valid: false, coupon: null };
      }
    } catch (err) {
      setCouponStatus("invalid");
      setCouponData(null);
      if (err.message && !err.message.includes("404")) {
        console.log(err.message);
        showToast(err.message, "error");
      }
      return { valid: false, coupon: null };
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Handle coupon input change
  const handleCouponChange = (e) => {
    setCouponCode(e.target.value);
    setCouponStatus(null);
    setCouponData(null);
  };

  // Handle coupon blur - validate without passing event
  const handleCouponBlur = () => {
    if (couponCode.trim()) {
      validateCoupon();
    }
  };

  // Filter categories based on search
  const filteredCategories = categories.filter((cat) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    if (cat.name.toLowerCase().includes(query)) return true;
    return cat.sub_categories?.some((sc) =>
      sc.name.toLowerCase().includes(query)
    );
  });

  // Filter sub-categories based on search
  const filterSubCategories = (subCategories) => {
    if (!searchQuery.trim()) return subCategories;
    const query = searchQuery.toLowerCase();
    return subCategories.filter((sc) =>
      sc.name.toLowerCase().includes(query)
    );
  };

  // Validate inputs
  const validateInputs = () => {
    if (selectedSubCategories.length === 0) {
      showToast(
        lang === "ar"
          ? "يرجى اختيار فئة واحدة على الأقل"
          : "Please select at least one category",
        "error"
      );
      return false;
    }

    if (selectedSpecialisations.length === 0) {
      showToast(
        lang === "ar"
          ? "يرجى اختيار تخصص واحد على الأقل"
          : "Please select at least one specialisation",
        "error"
      );
      return false;
    }

    const minNum = parseFloat(minAmount);
    const maxNum = parseFloat(maxAmount);

    if (isNaN(minNum) || minNum < 0 || minNum > 1000000) {
      showToast(t.invalidAmount, "error");
      return false;
    }

    if (isNaN(maxNum) || maxNum < 0 || maxNum > 1000000) {
      showToast(t.invalidAmount, "error");
      return false;
    }

    if (maxNum < minNum) {
      showToast("Max amount must be greater than or equal to min amount", "error");
      return false;
    }

    // If initial work amount exists, max can only increase (not decrease)
    if (initialWorkAmount && initialWorkAmount.max) {
      if (maxNum < initialWorkAmount.max) {
        const currentLang = localStorage.getItem("app_language") || "en";
        const translations = {
          en: "Max amount can only be increased",
          ar: "يمكن زيادة المبلغ الأقصى فقط",
        };
        showToast(translations[currentLang], "error");
        return false;
      }
    }

    return true;
  };

  // Open Stripe payment modal
  const openStripePayment = (clientSecret, publishableKey, orderDetails) => {
    setStripeClientSecret(clientSecret);
    setStripePublishableKey(publishableKey);
    setStripeOrderDetails(orderDetails);
    setShowStripeModal(true);
  };

  const closeStripeModal = () => {
    setShowStripeModal(false);
    setStripeClientSecret(null);
    setStripePublishableKey(null);
    setStripeOrderDetails(null);
    setPaymentProcessing(false);
    setShowPaymentOverlay(false);
  };

  // Handle payment
  const handlePayment = async () => {
    if (!validateInputs()) return;

    const final = calculateFinalAmount();
    if (final > 0 && final < 2) {
      showToast(t.minPaymentAmount, "error");
      return;
    }

    try {
      setPaymentProcessing(true);
      const payload = {
        sub_categories: selectedSubCategories,
        specialisations: selectedSpecialisations,
        work_amount: {
          min: parseFloat(minAmount),
          max: parseFloat(maxAmount),
        },
      };

      // Include coupon only if it's valid
      // If coupon is entered but not validated yet, validate it first
      if (couponCode.trim()) {
        let isValidCoupon = false;
        let validCouponData = null;
        
        if (couponStatus === "valid" && couponData) {
          isValidCoupon = true;
          validCouponData = couponData;
        } else {
          // Validate coupon
          const validationResult = await validateCoupon(couponCode.trim());
          isValidCoupon = validationResult.valid;
          validCouponData = validationResult.coupon;
        }
        
        if (isValidCoupon && validCouponData) {
          payload.coupon = couponCode.trim();
        }
      }

      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/sub-category/change`,
        "POST",
        JSON.stringify(payload),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200 && data.response) {
        const orderDetails = data.response;
        const amountPaid = orderDetails.amount_paid || 0;
        
        // If amount is 0, just refresh without opening Razorpay
        if (amountPaid === 0 || amountPaid <= 0) {
          showToast(t.paymentSuccess, "success");
          setShowSuccessOverlay(true);
          setPaymentProcessing(false);
          setTimeout(() => {
            if (onPaymentSuccess) {
              onPaymentSuccess();
            } else {
              window.location.reload();
            }
          }, 2000);
          return;
        }
        
        // Open Stripe payment modal for non-zero amounts
        const clientSecret = orderDetails.client_secret;
        const publishableKey = orderDetails.stripe_publishable_key;
        if (clientSecret && publishableKey) {
          openStripePayment(clientSecret, publishableKey, orderDetails);
        } else {
          showToast(t.paymentFailed, "error");
        }
      } else if (data && data.response_code === 201) {
        // No changes detected
        showToast(t.paymentSuccess, "success");
        setShowSuccessOverlay(true);
        setTimeout(() => {
          if (onPaymentSuccess) {
            onPaymentSuccess();
          } else {
            window.location.reload();
          }
        }, 2000);
      } else {
        showToast(data.message || "Something went wrong", "error");
      }
    } catch (err) {
      console.error("Payment error:", err);
      showToast(err.message || "Payment failed", "error");
    } finally {
      setPaymentProcessing(false);
    }
  };


  if (fetchingCategories) {
    return (
      <div className="category-payment-loading">
        <div className="spinner"></div>
        <p>{lang === "ar" ? "جاري التحميل..." : "Loading..."}</p>
      </div>
    );
  }

  const total = calculateTotal();
  const discount = calculateDiscount();
  const finalAmount = calculateFinalAmount();

  return (
    <div className="category-payment-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Selected Sub Categories */}
      {selectedSubCategories.length > 0 && (
        <div className="selected-categories-container">
          <div className="selected-categories-scroll">
            {selectedSubCategories.map((subCatId) => {
              const isDisabled = disabledSubCategories.includes(subCatId);
              return (
                <div
                  key={subCatId}
                  className={`selected-category-chip ${isDisabled ? "disabled" : ""}`}
                >
                  <span>{getSubCategoryName(subCatId)}</span>
                  {!isDisabled && (
                    <button
                      type="button"
                      onClick={() => removeSubCategory(subCatId)}
                      className="remove-category-btn"
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Specialisations */}
      {selectedSpecialisations.length > 0 && (
        <div className="selected-categories-container">
          <div className="selected-categories-scroll">
            {selectedSpecialisations.map((specId) => {
              const isDisabled = disabledSpecialisations.includes(specId);
              const spec = allSpecialisations.find((s) => s._id === specId);
              if (!spec) return null;
              return (
                <div
                  key={specId}
                  className={`selected-category-chip ${isDisabled ? "disabled" : ""}`}
                >
                  <span>{spec.name}</span>
                  {!isDisabled && (
                    <button
                      type="button"
                      onClick={() => removeSpecialisation(specId)}
                      className="remove-category-btn"
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="categories-list">
        {filteredCategories.map((category) => (
          <div key={category._id} className="category-item">
            <div
              className="category-header"
              onClick={() => toggleCategory(category._id)}
            >
              <div className="category-checkbox-wrapper">
                <input
                  type="checkbox"
                  checked={category.sub_categories?.some((sc) =>
                    selectedSubCategories.includes(sc._id)
                  )}
                  onChange={() => {}}
                  className="category-checkbox"
                />
              </div>
              <span className="category-name">{category.name}</span>
              <span className="category-arrow">
                {expandedCategories[category._id] ? "▲" : "▼"}
              </span>
            </div>

            {expandedCategories[category._id] && (
              <div className="sub-categories-container">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder={t.searchCategories}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="category-search-input"
                  />
                  <span className="search-icon">🔍</span>
                </div>

                <div className="sub-categories-list">
                  {filterSubCategories(category.sub_categories || []).map(
                    (subCategory) => {
                      const isSelected = selectedSubCategories.includes(
                        subCategory._id
                      );
                      const isDisabled = disabledSubCategories.includes(
                        subCategory._id
                      );
                      return (
                        <label
                          key={subCategory._id}
                          className={`sub-category-item ${
                            isSelected ? "selected" : ""
                          } ${isDisabled ? "disabled" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSubCategory(subCategory._id)}
                            disabled={isDisabled}
                            className="sub-category-checkbox"
                          />
                          <span className="sub-category-name">
                            {subCategory.name}
                          </span>
                        </label>
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Specialisations List */}
      {allSpecialisations && allSpecialisations.length > 0 && (
        <div className="categories-list">
          <div className="category-item">
            <div className="category-header">
              <span className="category-name">
                {lang === "ar" ? "التخصصات" : "Specialisations"}
              </span>
            </div>
            <div className="sub-categories-container">
              <div className="sub-categories-list">
                {allSpecialisations.map((spec) => {
                  const isSelected = selectedSpecialisations.includes(spec._id);
                  const isDisabled = disabledSpecialisations.includes(spec._id);
                  return (
                    <label
                      key={spec._id}
                      className={`sub-category-item ${
                        isSelected ? "selected" : ""
                      } ${isDisabled ? "disabled" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSpecialisation(spec._id)}
                        disabled={isDisabled}
                        className="sub-category-checkbox"
                      />
                      <span className="sub-category-name">
                        {spec.name}
                        {typeof spec.price === "number" && (
                          <> (AED {spec.price})</>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Min/Max Work Amount */}
      <div className="work-amount-section">
        <div className="amount-input-group">
          <label>{t.minWorkAmount}</label>
          <input
            type="number"
            placeholder={t.enterMinAmount}
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            className="amount-input"
            min="0"
            max="1000000"
          />
        </div>
        <div className="amount-input-group">
          <label>{t.maxWorkAmount}</label>
          <input
            type="number"
            placeholder={t.enterMaxAmount}
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            className="amount-input"
            min="0"
            max="1000000"
          />
        </div>
      </div>

      {/* Coupon Code */}
      <div className="coupon-section">
        <label className="coupon-label">{t.couponCode}</label>
        <div className="coupon-input-wrapper">
          <input
            type="text"
            placeholder={t.enterCoupon}
            value={couponCode}
            onChange={handleCouponChange}
            onBlur={handleCouponBlur}
            className={`coupon-input ${
              couponStatus === "valid"
                ? "valid"
                : couponStatus === "invalid"
                ? "invalid"
                : ""
            }`}
            disabled={validatingCoupon}
          />
          {validatingCoupon && (
            <span className="coupon-loading">...</span>
          )}
          {couponStatus === "valid" && !validatingCoupon && (
            <span className="coupon-checkmark">✓</span>
          )}
          {couponStatus === "invalid" && !validatingCoupon && (
            <span className="coupon-cross">×</span>
          )}
        </div>
        {couponStatus === "valid" && couponData && (
          <p className="coupon-success-message">{t.couponApplied}</p>
        )}
        {couponStatus === "invalid" && (
          <p className="coupon-error-message">{t.couponNotAvailable}</p>
        )}
      </div>

      {/* Payment Summary */}
      <div className="payment-summary">
        <div className="summary-row">
          <span>{t.total}:</span>
          <span>AED {total.toFixed(2)}</span>
        </div>
        <div className="summary-row">
          <span>{t.discount}:</span>
          <span>AED {discount.toFixed(2)}</span>
        </div>
        <div className="summary-row total-row">
          <span>{t.subTotal}:</span>
          <span>AED {finalAmount.toFixed(2)}</span>
        </div>
      </div>

      {/* Pay Now / Save Button */}
      <button
        className="pay-now-btn"
        onClick={handlePayment}
        disabled={paymentProcessing || validatingCoupon}
      >
        {paymentProcessing ? (
          <>
            <span className="loader"></span>
            <span>{t.processingPayment}</span>
          </>
        ) : (
          finalAmount > 0 ? t.payNow : t.save
        )}
      </button>

      {/* Payment Loading Overlay */}
      {showPaymentOverlay && (
        <div className="payment-overlay">
          <div className="payment-overlay-content">
            <div className="spinner"></div>
            <p>{t.processingPayment}</p>
            <p className="overlay-subtitle">
              {lang === "ar"
                ? "يرجى إكمال الدفع في نافذة الدفع"
                : "Please complete the payment in the payment window"}
            </p>
          </div>
        </div>
      )}

      {/* Success Overlay - shown after payment success before reload */}
      {showSuccessOverlay && (
        <div className="payment-overlay payment-success-overlay">
          <div className="payment-overlay-content payment-success-overlay-content">
            <div className="success-checkmark" aria-hidden="true"></div>
            <p>{t.paymentSuccess}</p>
            <p className="overlay-subtitle">
              {lang === "ar" ? "جاري إعادة التحميل..." : "Reloading..."}
            </p>
          </div>
        </div>
      )}

      {/* Stripe Payment Modal */}
      {showStripeModal && stripeClientSecret && stripePublishableKey && stripeOrderDetails && (
        <StripePaymentModal
          clientSecret={stripeClientSecret}
          publishableKey={stripePublishableKey}
          orderDetails={stripeOrderDetails}
          onSuccess={() => {
            closeStripeModal();
            showToast(t.paymentSuccess, "success");
            setShowSuccessOverlay(true);
            const reloadDelay = 2000;
            setTimeout(() => {
              if (onPaymentSuccess) {
                onPaymentSuccess();
              } else {
                window.location.reload();
              }
            }, reloadDelay);
          }}
          onCancel={closeStripeModal}
          onFailure={(msg) => {
            closeStripeModal();
            showToast(msg || t.paymentFailed, "error");
          }}
          setShowPaymentOverlay={setShowPaymentOverlay}
          apiFetch={apiFetch}
          USER_BASE_URL={USER_BASE_URL}
          VERSION={VERSION}
          lang={lang}
        />
      )}
    </div>
  );
}

// Stripe Payment Modal Component
function StripePaymentModal({
  clientSecret,
  publishableKey,
  orderDetails,
  onSuccess,
  onCancel,
  onFailure,
  setShowPaymentOverlay,
  apiFetch,
  USER_BASE_URL,
  VERSION,
  lang,
}) {
  const stripePromise = loadStripe(publishableKey);
  const t = {
    en: { confirm: "Pay", cancel: "Cancel", processing: "Processing..." },
    ar: { confirm: "ادفع", cancel: "إلغاء", processing: "جاري المعالجة..." },
  }[lang];

  return (
    <div className="stripe-modal-overlay" onClick={onCancel}>
      <div className="stripe-modal-content" onClick={(e) => e.stopPropagation()}>
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <StripePaymentForm
            clientSecret={clientSecret}
            orderDetails={orderDetails}
            onSuccess={onSuccess}
            onCancel={onCancel}
            onFailure={onFailure}
            setShowPaymentOverlay={setShowPaymentOverlay}
            apiFetch={apiFetch}
            USER_BASE_URL={USER_BASE_URL}
            VERSION={VERSION}
            t={t}
          />
        </Elements>
      </div>
    </div>
  );
}

function StripePaymentForm({
  clientSecret,
  orderDetails,
  onSuccess,
  onCancel,
  onFailure,
  setShowPaymentOverlay,
  apiFetch,
  USER_BASE_URL,
  VERSION,
  t,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setConfirming(true);
    setShowPaymentOverlay(true);

    try {
      // Required: submit elements first before confirmPayment (Stripe deferred flow)
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setShowPaymentOverlay(false);
        setConfirming(false);
        onFailure(submitError?.message || "Validation failed");
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin + "/",
        },
        redirect: "if_required",
      });

      if (error) {
        setShowPaymentOverlay(false);
        setConfirming(false);
        onFailure(error?.message || "Payment failed");
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // Keep overlay visible until server verification completes
        const verifyData = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/sub-category/change-verify-payment`,
          "POST",
          JSON.stringify({
            payment_intent_id: paymentIntent.id,
            id: orderDetails._id,
          }),
          { "Content-Type": "application/json" }
        );

        setShowPaymentOverlay(false);
        setConfirming(false);

        if (verifyData && verifyData.response_code === 200) {
          onSuccess();
        } else {
          onFailure(verifyData?.message || "Verification failed");
        }
      } else {
        setShowPaymentOverlay(false);
        setConfirming(false);
        onFailure(paymentIntent?.last_payment_error?.message || "Payment could not be completed");
      }
    } catch (err) {
      console.error("Stripe payment error:", err);
      setShowPaymentOverlay(false);
      setConfirming(false);
      onFailure(err?.message || "Payment failed");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <h3>Complete Payment</h3>
      <div className="stripe-card-element-wrapper">
        <PaymentElement />
      </div>
      <div className="stripe-modal-actions">
        <button type="button" onClick={onCancel} className="stripe-cancel-btn">
          {t.cancel}
        </button>
        <button type="submit" disabled={!stripe || confirming} className="stripe-confirm-btn">
          {confirming ? t.processing : t.confirm}
        </button>
      </div>
    </form>
  );
}
